import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Users, BookOpen, Rocket, Plus, ArrowDownAZ, Clock, CalendarClock } from 'lucide-react'
import { supabase } from '../lib/supabaseClient'
import AppShell from '../components/AppShell.jsx'
import Card from '../components/ui/Card.jsx'
import Badge from '../components/ui/Badge.jsx'
import Button from '../components/ui/Button.jsx'
import Skeleton from '../components/ui/Skeleton.jsx'

function StatCard({ icon: Icon, label, value, loading, active, onClick }) {
  return (
    <Card
      hover
      onClick={onClick}
      className={`p-5 ${onClick ? 'cursor-pointer' : ''} ${active ? 'border-blue/50 ring-1 ring-blue/30' : ''}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-md bg-blue/10 flex items-center justify-center text-sky">
          <Icon size={18} />
        </div>
        <span className="text-[13px] text-muted font-medium">{label}</span>
      </div>
      {loading ? <Skeleton className="h-8 w-16" /> : <div className="font-display font-bold text-[28px]">{value}</div>}
    </Card>
  )
}

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'published', label: 'Published' }
]
const SORTS = [
  { key: 'recent', label: 'Recently updated', icon: Clock },
  { key: 'newest', label: 'Newest first', icon: CalendarClock },
  { key: 'alpha', label: 'A → Z', icon: ArrowDownAZ }
]

export default function Dashboard() {
  const [stats, setStats] = useState(null)
  const [projects, setProjects] = useState(null)
  const [filter, setFilter] = useState('all')
  const [sortBy, setSortBy] = useState('recent')

  useEffect(() => {
    let cancelled = false
    async function load() {
      const [{ count: clientCount }, { data: allProjects }] = await Promise.all([
        supabase.from('clients').select('*', { count: 'exact', head: true }),
        supabase.from('projects').select('*, clients(name)').order('updated_at', { ascending: false }).limit(200)
      ])
      if (cancelled) return
      const list = allProjects ?? []
      setStats({
        clients: clientCount ?? 0,
        projects: list.length,
        published: list.filter((p) => p.status === 'published').length
      })
      setProjects(list)
    }
    load()
    return () => { cancelled = true }
  }, [])

  const visible = useMemo(() => {
    if (!projects) return null
    let list = filter === 'all' ? projects : projects.filter((p) => p.status === filter)
    list = [...list].sort((a, b) => {
      if (sortBy === 'alpha') return a.title.localeCompare(b.title)
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at)
      return new Date(b.updated_at) - new Date(a.updated_at) // 'recent' (default)
    })
    return list
  }, [projects, filter, sortBy])

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-bold text-[24px]">Dashboard</h1>
          <p className="text-muted text-[14px] mt-1">Everything you've built for your clients, in one place.</p>
        </div>
        <Link to="/clients"><Button icon={Plus}>New client</Button></Link>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <Link to="/clients"><StatCard icon={Users} label="Clients" value={stats?.clients} loading={!stats} /></Link>
        <StatCard icon={BookOpen} label="Flipbooks" value={stats?.projects} loading={!stats} active={filter === 'all'} onClick={() => setFilter('all')} />
        <StatCard icon={Rocket} label="Published" value={stats?.published} loading={!stats} active={filter === 'published'} onClick={() => setFilter('published')} />
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-1 p-1 rounded-md bg-panel2 border border-line">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-sm text-[13px] font-medium transition-colors duration-150 ${
                filter === f.key ? 'bg-panel text-ink shadow-elev1' : 'text-muted hover:text-ink'
              }`}
            >
              {f.label}
              {projects && (
                <span className="ml-1.5 text-[11px] text-muted-2">
                  {f.key === 'all' ? projects.length : projects.filter((p) => p.status === f.key).length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5">
          {SORTS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSortBy(s.key)}
              title={s.label}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12.5px] font-medium border transition-colors duration-150 ${
                sortBy === s.key ? 'border-blue/40 text-sky bg-blue/10' : 'border-line text-muted-2 hover:text-muted'
              }`}
            >
              <s.icon size={13} />
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {visible === null ? (
        <div className="grid grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : visible.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-muted text-[14px] mb-4">
            {filter === 'all' ? 'No flipbooks yet — create your first client to get started.' : `No ${filter} flipbooks yet.`}
          </p>
          <Link to="/clients"><Button icon={Plus} className="mx-auto">New client</Button></Link>
        </Card>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {visible.map((p) => (
            <Link key={p.id} to={`/editor/${p.id}`}>
              <Card hover className="p-4 cursor-pointer h-full">
                <div className="aspect-[4/3] rounded-md bg-panel2 mb-3 flex items-center justify-center text-muted-2">
                  <BookOpen size={28} />
                </div>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-[14px] truncate">{p.title}</div>
                    <div className="text-[12px] text-muted-2 truncate">{p.clients?.name}</div>
                  </div>
                  <Badge tone={p.status === 'published' ? 'published' : 'draft'}>{p.status}</Badge>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </AppShell>
  )
}
